import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Phone,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import {
  EMPLOYMENT_STATUS_LABELS,
  INCOME_BANDS,
  formatLongDate,
  formatShortDate,
} from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn, groupNairaInput, money, nairaInputToKobo, parseMoney } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';

interface Product {
  id: string;
  name: string;
  description: string | null;
  minimumAmount: string;
  maximumAmount: string;
  minimumInstallments: number;
  maximumInstallments: number;
  repaymentFrequency: string;
  requiredGuarantors: number;
}

interface Preview {
  principal: string;
  totalRepayable: string;
  totalInterest: string;
  totalFees: string;
  installmentCount: number;
  regularInstallment: string;
  firstDueDate: string;
  finalDueDate: string;
  installments: Array<{ installmentNumber: number; dueDate: string; amountDue: string }>;
}

interface GuarantorForm {
  fullName: string;
  relationship: string;
  phone: string;
  alternatePhone: string;
  email: string;
  address: string;
  occupation: string;
  employerName: string;
  membershipNumber: string;
}

const EMPTY_GUARANTOR: GuarantorForm = {
  fullName: '',
  relationship: '',
  phone: '',
  alternatePhone: '',
  email: '',
  address: '',
  occupation: '',
  employerName: '',
  membershipNumber: '',
};

const STEPS = ['Loan', 'Your circumstances', 'Guarantors', 'Review'] as const;

export default function ApplyForLoan() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);

  const [form, setForm] = useState({
    productId: '',
    amount: '',
    installmentCount: 6,
    purpose: '',
    employmentStatus: '',
    employerName: '',
    monthlyIncomeBand: '',
    repaymentSource: '',
    hasOtherLoans: false,
    otherObligations: '',
  });

  const [guarantors, setGuarantors] = useState<GuarantorForm[]>([]);

  const { data: products = [] } = useQuery({
    queryKey: ['public-products'],
    queryFn: () => api.get<Product[]>('/loan-products/public'),
  });

  const product = useMemo(
    () => products.find((p) => p.id === form.productId),
    [products, form.productId],
  );

  useEffect(() => {
    if (!form.productId && products.length > 0) {
      const first = products[0]!;
      setForm((f) => ({ ...f, productId: first.id, installmentCount: first.minimumInstallments }));
    }
  }, [products, form.productId]);

  // Match the guarantor slots to whatever the chosen product requires.
  useEffect(() => {
    if (!product) return;
    setGuarantors((current) => {
      if (current.length >= product.requiredGuarantors) return current;
      const needed = product.requiredGuarantors - current.length;
      return [...current, ...Array.from({ length: needed }, () => ({ ...EMPTY_GUARANTOR }))];
    });
  }, [product]);

  const amountIsValid = useMemo(() => {
    if (!product || !form.amount) return false;
    const kobo = nairaInputToKobo(form.amount);
    return kobo >= parseMoney(product.minimumAmount) && kobo <= parseMoney(product.maximumAmount);
  }, [product, form.amount]);

  const { data: preview } = useQuery({
    queryKey: ['schedule-preview', form.productId, form.amount, form.installmentCount],
    queryFn: () =>
      api.post<Preview>('/loan-applications/preview', {
        loanProductId: form.productId,
        amount: form.amount,
        installmentCount: form.installmentCount,
      }),
    enabled: Boolean(form.productId) && amountIsValid,
  });

  const guarantorsComplete = useMemo(() => {
    const required = product?.requiredGuarantors ?? 0;
    const complete = guarantors.filter(
      (g) => g.fullName.trim() && g.relationship.trim() && g.phone.trim() && g.address.trim(),
    );
    return complete.length >= required;
  }, [guarantors, product]);

  const stepComplete = [
    amountIsValid && form.purpose.trim().length >= 10,
    Boolean(form.employmentStatus) && form.repaymentSource.trim().length >= 5,
    guarantorsComplete,
    confirmed,
  ];

  const submit = useMutation({
    mutationFn: async () => {
      const application = await api.post<{ id: string }>('/loan-applications', {
        loanProductId: form.productId,
        requestedAmount: form.amount,
        installmentCount: form.installmentCount,
        purpose: form.purpose,
        employmentStatus: form.employmentStatus,
        employerName: form.employerName || undefined,
        monthlyIncomeBand: form.monthlyIncomeBand || undefined,
        repaymentSource: form.repaymentSource,
        hasOtherLoans: form.hasOtherLoans,
        otherObligations: form.otherObligations || undefined,
        guarantors: guarantors
          .filter((g) => g.fullName.trim() && g.phone.trim())
          .map((g) => ({
            fullName: g.fullName,
            relationship: g.relationship,
            phone: g.phone,
            alternatePhone: g.alternatePhone || undefined,
            email: g.email || undefined,
            address: g.address,
            occupation: g.occupation || undefined,
            employerName: g.employerName || undefined,
            membershipNumber: g.membershipNumber || undefined,
          })),
      });

      return api.post<{ message: string }>('/loan-applications/submit', {
        applicationId: application.id,
        confirmationAccepted: true,
        acceptedTermsVersion: 'v1.0',
      });
    },
    onSuccess: (result) => {
      toast.success(result.message ?? 'Application submitted.');
      navigate('/portal/applications');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Something went wrong.');
      }
    },
  });

  const setGuarantor = (index: number, key: keyof GuarantorForm, value: string) =>
    setGuarantors((current) =>
      current.map((g, i) => (i === index ? { ...g, [key]: value } : g)),
    );

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Interest-free borrowing"
        title="Apply for a loan"
      />

      {/* Stepper */}
      <div className="mb-6 flex flex-wrap gap-1.5">
        {STEPS.map((label, index) => (
          <button
            key={label}
            type="button"
            onClick={() => setStep(index)}
            className={cn(
              'flex-1 rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors',
              index === step
                ? 'border-primary bg-primary text-primary-foreground shadow-brand'
                : stepComplete[index]
                  ? 'border-success/30 bg-success-soft text-success-soft-foreground'
                  : 'border-border bg-background text-foreground-secondary hover:bg-accent',
            )}
          >
            {stepComplete[index] && index !== step && <Check size={12} className="mr-1 inline" />}
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          {/* --- Step 1: the loan --- */}
          {step === 0 && (
            <Card>
              <CardContent className="space-y-5 p-6">
                <Field label="Loan product" htmlFor="product" required>
                  <Select
                    id="product"
                    value={form.productId}
                    onChange={(e) => {
                      const next = products.find((p) => p.id === e.target.value);
                      setForm((f) => ({
                        ...f,
                        productId: e.target.value,
                        installmentCount: next?.minimumInstallments ?? f.installmentCount,
                      }));
                    }}
                  >
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>

                {product?.description && (
                  <p className="text-sm leading-relaxed text-foreground-secondary">
                    {product.description}
                  </p>
                )}

                <Field
                  label="Amount (₦)"
                  htmlFor="amount"
                  required
                  error={fieldErrors['requestedAmount']}
                  hint={
                    product
                      ? parseMoney(product.minimumAmount) > 0n
                        ? `Between ${money(product.minimumAmount)} and ${money(product.maximumAmount)}`
                        : `Up to ${money(product.maximumAmount)} — borrow only what you need`
                      : undefined
                  }
                >
                  <Input
                    id="amount"
                    inputMode="decimal"
                    className="numeric text-lg"
                    value={form.amount}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, amount: groupNairaInput(e.target.value) }))
                    }
                    placeholder="300,000"
                    invalid={Boolean(form.amount) && !amountIsValid}
                  />
                </Field>

                <Field
                  label="Number of repayments"
                  htmlFor="installments"
                  required
                  hint={
                    product
                      ? `${product.minimumInstallments}–${product.maximumInstallments} ${product.repaymentFrequency.toLowerCase()} payments`
                      : undefined
                  }
                >
                  <Input
                    id="installments"
                    type="number"
                    className="numeric"
                    min={product?.minimumInstallments ?? 1}
                    max={product?.maximumInstallments ?? 60}
                    value={form.installmentCount}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, installmentCount: Number(e.target.value) }))
                    }
                  />
                </Field>

                <Field
                  label="What is the loan for?"
                  htmlFor="purpose"
                  required
                  error={fieldErrors['purpose']}
                  hint="At least 10 characters."
                >
                  <Textarea
                    id="purpose"
                    rows={3}
                    value={form.purpose}
                    onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
                    placeholder="e.g. School fees for the coming session."
                  />
                </Field>
              </CardContent>
            </Card>
          )}

          {/* --- Step 2: circumstances --- */}
          {step === 1 && (
            <Card>
              <CardContent className="space-y-5 p-6">
                <p className="text-sm leading-relaxed text-foreground-secondary">
                  UNIFEMGA lends without interest, so there is no rate to price your risk into.
                  These questions are how we judge what you can comfortably repay.
                </p>

                <Field
                  label="Employment status"
                  htmlFor="employment"
                  required
                  error={fieldErrors['employmentStatus']}
                >
                  <Select
                    id="employment"
                    value={form.employmentStatus}
                    onChange={(e) => setForm((f) => ({ ...f, employmentStatus: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Employer or business" htmlFor="employer">
                    <Input
                      id="employer"
                      value={form.employerName}
                      onChange={(e) => setForm((f) => ({ ...f, employerName: e.target.value }))}
                    />
                  </Field>
                  <Field label="Monthly income" htmlFor="income" hint="A band, not an exact figure.">
                    <Select
                      id="income"
                      value={form.monthlyIncomeBand}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, monthlyIncomeBand: e.target.value }))
                      }
                    >
                      <option value="">Prefer not to say</option>
                      {INCOME_BANDS.map((band) => (
                        <option key={band} value={band}>
                          {band}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <Field
                  label="How will you repay?"
                  htmlFor="repaymentSource"
                  required
                  error={fieldErrors['repaymentSource']}
                  hint="e.g. Monthly salary, paid on the 25th."
                >
                  <Textarea
                    id="repaymentSource"
                    rows={2}
                    value={form.repaymentSource}
                    onChange={(e) => setForm((f) => ({ ...f, repaymentSource: e.target.value }))}
                  />
                </Field>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted p-4">
                  <input
                    type="checkbox"
                    checked={form.hasOtherLoans}
                    onChange={(e) => setForm((f) => ({ ...f, hasOtherLoans: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm text-foreground-secondary">
                    I have other loans or regular financial commitments elsewhere.
                  </span>
                </label>

                {form.hasOtherLoans && (
                  <Field
                    label="Tell us about them"
                    htmlFor="otherObligations"
                    hint="UNIFEMGA cannot see another lender's records, so we have to ask."
                  >
                    <Textarea
                      id="otherObligations"
                      rows={2}
                      value={form.otherObligations}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, otherObligations: e.target.value }))
                      }
                      placeholder="e.g. Cooperative loan, ₦20,000 monthly, 8 months remaining."
                    />
                  </Field>
                )}
              </CardContent>
            </Card>
          )}

          {/* --- Step 3: guarantors --- */}
          {step === 2 && (
            <div className="space-y-4">
              <Alert tone="info" title="Speak to them first">
                UNIFEMGA will <strong>telephone each guarantor</strong> to confirm they agree before
                your application is decided. Please make sure you have spoken to them already, and
                that the numbers below are ones they will answer.
                <br />
                <br />
                They do <strong>not</strong> need a UNIFEMGA portal account — anyone willing to
                stand for you can be named here.
              </Alert>

              {guarantors.map((guarantor, index) => (
                <Card key={index}>
                  <CardContent className="space-y-5 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading font-bold text-foreground">
                        Guarantor {index + 1}
                        {index < (product?.requiredGuarantors ?? 0) && (
                          <span className="ml-1 text-danger">*</span>
                        )}
                      </h3>
                      {guarantors.length > (product?.requiredGuarantors ?? 0) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setGuarantors((c) => c.filter((_, i) => i !== index))
                          }
                          aria-label="Remove guarantor"
                        >
                          <Trash2 size={14} className="text-danger" />
                        </Button>
                      )}
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Full name" htmlFor={`gname-${index}`} required>
                        <Input
                          id={`gname-${index}`}
                          value={guarantor.fullName}
                          onChange={(e) => setGuarantor(index, 'fullName', e.target.value)}
                        />
                      </Field>
                      <Field
                        label="How do you know them?"
                        htmlFor={`grel-${index}`}
                        required
                        hint="e.g. Colleague, brother, neighbour"
                      >
                        <Input
                          id={`grel-${index}`}
                          value={guarantor.relationship}
                          onChange={(e) => setGuarantor(index, 'relationship', e.target.value)}
                        />
                      </Field>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Phone number"
                        htmlFor={`gphone-${index}`}
                        required
                        hint="UNIFEMGA will call this number."
                      >
                        <Input
                          id={`gphone-${index}`}
                          className="numeric"
                          value={guarantor.phone}
                          onChange={(e) =>
                            setGuarantor(index, 'phone', e.target.value.replace(/[^\d+\s()-]/g, ''))
                          }
                          placeholder="0803 000 0000"
                        />
                      </Field>
                      <Field label="Another number" htmlFor={`galt-${index}`}>
                        <Input
                          id={`galt-${index}`}
                          className="numeric"
                          value={guarantor.alternatePhone}
                          onChange={(e) => setGuarantor(index, 'alternatePhone', e.target.value)}
                        />
                      </Field>
                    </div>

                    <Field label="Address" htmlFor={`gaddr-${index}`} required>
                      <Input
                        id={`gaddr-${index}`}
                        value={guarantor.address}
                        onChange={(e) => setGuarantor(index, 'address', e.target.value)}
                      />
                    </Field>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Occupation" htmlFor={`gocc-${index}`}>
                        <Input
                          id={`gocc-${index}`}
                          value={guarantor.occupation}
                          onChange={(e) => setGuarantor(index, 'occupation', e.target.value)}
                        />
                      </Field>
                      <Field label="Email" htmlFor={`gemail-${index}`}>
                        <Input
                          id={`gemail-${index}`}
                          type="email"
                          value={guarantor.email}
                          onChange={(e) => setGuarantor(index, 'email', e.target.value)}
                        />
                      </Field>
                    </div>

                    <Field
                      label="Membership number"
                      htmlFor={`gmem-${index}`}
                      hint="Only if they are also a UNIFEMGA member. Leave blank otherwise."
                    >
                      <Input
                        id={`gmem-${index}`}
                        className="numeric uppercase"
                        value={guarantor.membershipNumber}
                        onChange={(e) =>
                          setGuarantor(index, 'membershipNumber', e.target.value.toUpperCase())
                        }
                        placeholder="UNF/2019/0142"
                      />
                    </Field>
                  </CardContent>
                </Card>
              ))}

              {guarantors.length < 5 && (
                <Button
                  variant="outline"
                  full
                  onClick={() => setGuarantors((c) => [...c, { ...EMPTY_GUARANTOR }])}
                >
                  <Plus size={16} /> Add another guarantor
                </Button>
              )}
            </div>
          )}

          {/* --- Step 4: review --- */}
          {step === 3 && (
            <Card>
              <CardContent className="space-y-5 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground">
                  Check before submitting
                </h3>

                <Summary label="Product" value={product?.name ?? '—'} />
                <Summary label="Amount" value={money(nairaInputToKobo(form.amount).toString())} />
                <Summary label="Repayments" value={`${form.installmentCount} monthly`} />
                <Summary label="Purpose" value={form.purpose} />
                <Summary
                  label="Guarantors"
                  value={
                    guarantors
                      .filter((g) => g.fullName.trim())
                      .map((g) => `${g.fullName} (${g.phone})`)
                      .join(', ') || '—'
                  }
                />

                <Alert tone="info">
                  <span className="inline-flex items-start gap-2">
                    <Phone size={14} className="mt-0.5 shrink-0" />
                    UNIFEMGA will telephone each guarantor to confirm before deciding.
                  </span>
                </Alert>

                <label className="flex cursor-pointer items-start gap-3 border-t border-border pt-5">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm text-foreground-secondary">
                    I confirm that the information provided is accurate, that I have spoken to
                    everyone named as a guarantor, and that I agree to the terms of this loan.
                  </span>
                </label>

                <Button
                  full
                  size="lg"
                  disabled={!confirmed || !stepComplete[0] || !stepComplete[1] || !stepComplete[2]}
                  loading={submit.isPending}
                  onClick={() => submit.mutate()}
                >
                  Submit application <ArrowRight size={16} />
                </Button>

                <p className="text-center text-xs text-foreground-secondary">
                  Your confirmation is recorded with the date, your IP address and the version of
                  the terms shown above.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft size={16} /> Back
            </Button>
            {step < STEPS.length - 1 && (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!stepComplete[step]}>
                Next <ArrowRight size={16} />
              </Button>
            )}
          </div>
        </div>

        {/* Live schedule */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-4">
            <Card className="surface-brand border-primary/20">
              <CardContent className="pt-6">
                <p className="eyebrow mb-4">What you will repay</p>

                {!amountIsValid ? (
                  <p className="text-sm text-foreground-secondary">
                    Enter an amount to see your schedule.
                  </p>
                ) : preview ? (
                  <>
                    <div className="mb-4 text-sm">
                      <Row label="You borrow" value={money(preview.principal)} bold />
                    </div>

                    <div className="rounded-xl bg-background p-4">
                      <p className="display-figure text-2xl text-foreground">
                        {money(preview.regularInstallment)}
                      </p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-foreground-secondary">
                        per month × {preview.installmentCount}
                      </p>
                    </div>

                    <dl className="mt-4 space-y-1.5 text-xs text-foreground-secondary">
                      <div className="flex justify-between">
                        <dt>First payment</dt>
                        <dd className="numeric font-semibold text-foreground">
                          {formatLongDate(new Date(preview.firstDueDate))}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt>Final payment</dt>
                        <dd className="numeric font-semibold text-foreground">
                          {formatLongDate(new Date(preview.finalDueDate))}
                        </dd>
                      </div>
                    </dl>

                  </>
                ) : (
                  <p className="text-sm text-foreground-secondary">Calculating…</p>
                )}
              </CardContent>
            </Card>

            {product && product.requiredGuarantors > 0 && (
              <Card>
                <CardContent className="flex items-start gap-3 p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
                    <Users size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {product.requiredGuarantors} guarantor
                      {product.requiredGuarantors === 1 ? '' : 's'} required
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed text-foreground-secondary">
                      Each will be telephoned by UNIFEMGA to confirm.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {preview && amountIsValid && step === 3 && (
              <Card>
                <CardContent className="max-h-64 overflow-y-auto px-0 pt-6">
                  <Table>
                    <thead>
                      <tr>
                        <Th>#</Th>
                        <Th>Due</Th>
                        <Th numeric>Amount</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.installments.map((installment) => (
                        <tr key={installment.installmentNumber}>
                          <Td className="numeric">{installment.installmentNumber}</Td>
                          <Td className="numeric text-foreground-secondary">
                            {formatShortDate(new Date(installment.dueDate))}
                          </Td>
                          <Td numeric>{money(installment.amountDue)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'success';
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-foreground-secondary">{label}</span>
      <span
        className={cn(
          'numeric',
          bold ? 'display-figure text-lg' : 'font-semibold',
          tone === 'success' ? 'text-success' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Summary({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success';
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border pb-3 last:border-0">
      <span className="text-sm text-foreground-secondary">{label}</span>
      <span
        className={cn(
          'max-w-md text-right text-sm font-semibold',
          tone === 'success' ? 'text-success' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}
